<template>
  <div v-if="data.employee">
    <div v-if="data.employee.email" class="mb-2">
      <div class="item__label">{{ $t(`Common.email`) }}</div>
      <div>{{ data.employee.email }}</div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`Common.phone`) }}</div>
      <div v-if="data.employee.phone">{{ data.employee.phone | phoneWithDDD }}</div>
      <router-link
        v-else-if="claims.employee['ROLE_PUT_EMPLOYEE_ID']"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addPhone`) }}
      </router-link>
      <div v-else>N/A</div>
    </div>

    <div class="mb-2">
      <div class="item__label text-capitalize">{{ $t(`Common.company`) }}</div>
      <div v-if="data.employee.company && data.employee.company.name">
        {{ data.employee.company.name }}
      </div>
      <div v-else>N/A</div>
    </div>

    <div class="mb-2">
      <div class="item__label text-capitalize">{{ $t(`Common.supervisor`) }}</div>
      <div v-if="data.employee.supervisor && data.employee.supervisor.name">
        {{ data.employee.supervisor.name }}
      </div>
      <router-link
        v-else-if="claims.employee['ROLE_PUT_EMPLOYEE_ID']"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addSupervisor`) }}
      </router-link>
      <div v-else>N/A</div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`Common.costCenter`) }}</div>
      <div v-if="isValidArray(data.costCenters)">
        <div v-if="data.costCenters.length > 4 && costCentersLimit">
          <span v-for="(item, index) in data.costCenters.slice(0, 4)" :key="index">
            <a
              v-if="claims.costCenter['ROLE_GET_COSTCENTER_ID']"
              class="accent--text"
              @click.prevent="change({
                type: 'costCenter',
                action: null
              }, {
                id: item.id,
                name: item.name.trim()
              })"
            >
              {{ item.name.trim() }}<span v-if="index !== (data.costCenters.slice(0, 4).length - 1)">, </span>
            </a>

            <span v-else>
              {{ item.name.trim() }}<span v-if="index !== (data.costCenters.slice(0, 4).length - 1)">, </span>
            </span>
          </span>

          <div>
            <a class="gray-8--text caption" @click="handleToggleCostCentersLimit">
              {{ $t(`${namespace}.viewMore`) }}
            </a>
          </div>
        </div>

        <div v-if="data.costCenters.length <= 4 || !costCentersLimit">
          <span v-for="(item, index) in data.costCenters" :key="index">
            <a
              v-if="claims.costCenter['ROLE_GET_COSTCENTER_ID']"
              class="accent--text"
              @click.prevent="change({
                type: 'costCenter',
                action: null
              }, {
                id: item.id,
                name: item.name.trim()
              })"
            >
              {{ item.name.trim() }}<span v-if="index !== (data.costCenters.length - 1)">, </span>
            </a>

            <span v-else>
              {{ item.name.trim() }}<span v-if="index !== (data.costCenters.length - 1)">, </span>
            </span>
          </span>

          <div v-if="data.costCenters.length > 4">
            <a class="gray-8--text caption" @click="handleToggleCostCentersLimit">
              {{ $t(`${namespace}.seeLess`) }}
            </a>
          </div>
        </div>
      </div>

      <router-link
        v-else-if="claims.employee['ROLE_PUT_EMPLOYEE_ID']"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addCostCenter`) }}
      </router-link>

      <div v-else>N/A</div>
    </div>

    <div class="mb-3">
      <div class="item__label text-capitalize">{{ $t(`Common.categories`) }}</div>

      <div v-if="isValidArray(data.employee.categoryIds)">
        <span
          v-for="category in data.employee.categoryIds "
          :key="category"
        >
          <!-- tip: 对品类有特殊处理 此处不显示poupa99，不知道什么背景 -->
          <v-tooltip v-if="getCategoryData(category)" v-show="getCategoryData(category) && getCategoryData(category).text !== '99Poupa'" slot="append" top min-width="177" max-width="177">
            <div slot="activator" class="label-category">
              <img :src="getCategoryData(category).img" alt="">
              <span>{{ getCategoryData(category).text }}</span>
            </div>

            <span>{{ getCategoryData(category).tooltip }}</span>
          </v-tooltip>
        </span>
      </div>

      <router-link
        v-else-if="claims.employee['ROLE_PUT_EMPLOYEE_ID']"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addCategories`) }}
      </router-link>

      <div v-else>N/A</div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`${namespace}.managementPlatformAccess`) }}</div>

      <div v-if="data.employee.v2Permission">
        {{ data.employee.v2Permission ? $t('Common.yes') : $t('Common.no') }}
      </div>

      <router-link
        v-else-if="claims.employee['ROLE_PUT_EMPLOYEE_ID']"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addPlatformAccess`) }}
      </router-link>

      <div v-else>N/A</div>
    </div>

    <div v-if="data.employee.webCallPermission || data.employee.thirdPartyWebCallPermission" class="mb-2">
      <div class="item__label text-capitalize">{{ $t(`${namespace}.webcall`) }}</div>

      <div v-if="data.employee.webCallPermission && data.employee.thirdPartyWebCallPermission">
        {{ $t(`${namespace}.webcallForEmployeesAndThirdParties`) }}
      </div>

      <div v-if="data.employee.webCallPermission && !data.employee.thirdPartyWebCallPermission">
        {{ $t(`${namespace}.webcallForEmployees`) }}
      </div>

      <div v-if="!data.employee.webCallPermission && data.employee.thirdPartyWebCallPermission">
        {{ $t(`${namespace}.webcallForThirdParties`) }}
      </div>

      <router-link
        v-else-if="
          !data.employee.webCallPermission &&
          !data.employee.thirdPartyWebCallPermission &&
          claims.employee['ROLE_PUT_EMPLOYEE_ID']
        "
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addWebCallPermission`) }}
      </router-link>

      <div
        v-if="
          !data.employee.webCallPermission &&
          !data.employee.thirdPartyWebCallPermission &&
          !claims.employee['ROLE_PUT_EMPLOYEE_ID']
        "
      >
        N/A
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isArray, objectToArray } from 'utils/functions'

export default {
  name: 'TabInfo',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapState({
      claims: ({ userClaims }) => userClaims.claims
    })
  },
  data () {
    return {
      namespace: 'Components.GlobalDrawer.employee.tabs',
      costCentersLimit: true
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    handleToggleCostCentersLimit () {
      this.costCentersLimit = !this.costCentersLimit
    },

    getCategoryData (category) {
      if (!category) return
      /* todo: ridesCategoriesList */
      return objectToArray(this.$store.state.commonConfig.categories).find(item => item.value === category)
    },

    change (drawer, data) {
      this.$emit('change', {
        drawer: {
          type: drawer.type,
          action: drawer.action
        },
        data: {
          id: data.id,
          name: data.name
        }
      })
    }
  }
}
</script>
